import React, { useState, useEffect, useRef } from "react";
import PassedTestModal from "../../Modals/PassedTestModal";
import FailedTestModal from "../../Modals/FailedTestModal";
import ChoiceQuestion from "./ChoiceQuestion";
import ExamProgressFloat from "./ExamProgressFloat";
import ExamTimerFloat from "./ExamTimerFloat";

const PASSED_TEST_MODAL_ID = "passed-test";
const FAILED_TEST_MODAL_ID = "failed-test";

const formatTimeRemaining = (totalSeconds) => {
  if (totalSeconds == null || totalSeconds < 0) return null;
  const m = Math.floor(totalSeconds / 60);
  const s = Math.max(0, totalSeconds % 60);
  return `${m}:${s.toString().padStart(2, "0")}`;
};

const CourseExamContent = ({
  questions,
  createLessonLog,
  currentLessonId,
  refetch,
  setCurrentLesson,
  getLesson,
  quizTimeMinutes,
}) => {
  const [myAnswers, setMyAnswers] = useState([]);
  const [examResult, setExamResult] = useState(null);
  const [showPassedModal, setShowPassedModal] = useState(false);
  const [showFailedModal, setShowFailedModal] = useState(false);
  const submittedRef = useRef(false);
  const myAnswersRef = useRef(myAnswers);
  myAnswersRef.current = myAnswers;

  const hasTimer =
    quizTimeMinutes != null &&
    quizTimeMinutes !== "" &&
    !Number.isNaN(Number(quizTimeMinutes)) &&
    Number(quizTimeMinutes) > 0;

  const initialSeconds = hasTimer ? Math.floor(Number(quizTimeMinutes) * 60) : null;
  const [timeRemainingSeconds, setTimeRemainingSeconds] = useState(initialSeconds);
  const timeExpired = timeRemainingSeconds !== null && timeRemainingSeconds <= 0;

  const handleSubmittingExam = async () => {
    if (submittedRef.current) return;
    submittedRef.current = true;
    const result = await createLessonLog({
      lesson_id: currentLessonId,
      answers: myAnswersRef.current,
    });
    if (!result.error) {
      const quizResults = result?.data?.data?.quiz_results;
      setExamResult(quizResults ?? null);
      if (quizResults?.passed) {
        setShowPassedModal(true);
      } else {
        setShowFailedModal(true);
      }
    } else {
      submittedRef.current = false;
    }
  };

  useEffect(() => {
    if (!hasTimer || timeRemainingSeconds == null || timeRemainingSeconds <= 0)
      return;
    const intervalId = setInterval(() => {
      setTimeRemainingSeconds((prev) => {
        if (prev <= 1) {
          clearInterval(intervalId);
          handleSubmittingExam();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(intervalId);
  }, [hasTimer]);

  useEffect(() => {
    const el = document.getElementById(PASSED_TEST_MODAL_ID);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (showPassedModal) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [showPassedModal]);

  useEffect(() => {
    const el = document.getElementById(PASSED_TEST_MODAL_ID);
    if (!el) return;
    const handleHidden = () => setShowPassedModal(false);
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, []);

  useEffect(() => {
    const el = document.getElementById(FAILED_TEST_MODAL_ID);
    if (!el) return;
    const bootstrapModal = window.bootstrap?.Modal?.getOrCreateInstance(el);
    if (!bootstrapModal) return;
    if (showFailedModal) {
      bootstrapModal.show();
    } else {
      bootstrapModal.hide();
    }
  }, [showFailedModal]);

  useEffect(() => {
    const el = document.getElementById(FAILED_TEST_MODAL_ID);
    if (!el) return;
    const handleHidden = () => setShowFailedModal(false);
    el.addEventListener("hidden.bs.modal", handleHidden);
    return () => el.removeEventListener("hidden.bs.modal", handleHidden);
  }, []);

  return (
    <div className="content-course-exam">
      <ExamProgressFloat
        answeredQuestionNumber={myAnswers.length}
        totalQuestionNumber={questions.length}
      />
      <ExamTimerFloat
        countdownFormatted={
          timeRemainingSeconds != null
            ? formatTimeRemaining(timeRemainingSeconds)
            : null
        }
      />
      <p className="font-color-grey font_20 mb-4">
        <span className="font-extrabold">{questions?.length}</span> سؤال{" "}
      </p>
      {questions?.map((question, i) => {
        const handleChangingAnswer = (e) => {
          let selectedAnswer = {
            id: question?.id,
            answer: e.target.value,
            answerd_at: new Date(),
          };
          const filteredAnswers = myAnswers.filter(
            (answer) => answer?.id != selectedAnswer?.id
          );
          setMyAnswers((prev) => [...filteredAnswers, selectedAnswer]);
        };

        return (
          <div className="card-style mb-4">
            <h1 className="font_16 font-color-grey">السؤال {++i}</h1>
            <ChoiceQuestion
              raidoName={question?.id}
              title={question?.question}
              answers={question?.answers}
              handleChangingAnswer={handleChangingAnswer}
            />
          </div>
        );
      })}

      <div className="card-style mb-4">
        <h1 className="font_20 font-color-grey mb-4">
          هل انتهيت من إجابة الاسئلة وتريد إنهاء الاختبار؟
        </h1>

        <button
          type="button"
          className="btn btn-main-color font-15 me-3"
          style={{ maxWidth: "240px" }}
          onClick={handleSubmittingExam}
          disabled={
            timeExpired || myAnswers?.length != questions?.length
          }
        >
          {timeExpired ? "انتهى الوقت" : "إنهاء الاختبار"}
        </button>
      </div>
      <PassedTestModal
        id={PASSED_TEST_MODAL_ID}
        isOpened={showPassedModal}
        correctAnswersCount={examResult?.correct_answers_count ?? 0}
        wrongAnswersCount={
          (examResult?.questions_count ?? 0) -
          (examResult?.correct_answers_count ?? 0)
        }
        quizPercentage={examResult?.quiz_percentage}
        onClick={async () => {
          window.location.reload(false);
        }}
      />
      <FailedTestModal
        id={FAILED_TEST_MODAL_ID}
        isOpened={showFailedModal}
        correctAnswersCount={examResult?.correct_answers_count ?? 0}
        wrongAnswersCount={
          (examResult?.questions_count ?? 0) -
          (examResult?.correct_answers_count ?? 0)
        }
        quizPercentage={examResult?.quiz_percentage}
        onClose={() => setShowFailedModal(false)}
      />
    </div>
  );
};

export default CourseExamContent;
